import React, { isValidElement, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconCheck, IconCloseLarge, IconErrorToast, IconInfoToast, IconLobbyUser, IconLobbyUsers, IconMessage, IconWarningToast } from '../../../base/icons/svg';
import Message from '../../../base/react/components/web/Message';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { NOTIFICATION_ICON, NOTIFICATION_TYPE } from '../../constants';
import { NotificationsTransitionContext } from '../NotificationsTransition';
/**
 * Secondary colors for notification icons.
 *
 * @type {{error, info, normal, success, warning}}
 */
const useStyles = makeStyles()((theme) => {
    return {
        container: {
            minWidth: '350px',
            maxWidth: '420px',
            backgroundColor: theme.palette.ui10,
            padding: '8px 16px 8px 20px',
            display: 'flex',
            position: 'relative',
            borderRadius: `${theme.shape.borderRadius}px`,
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.25)',
            marginBottom: theme.spacing(2),
            fontSize: '16px',
            '&:last-of-type': {
                marginBottom: 0
            },
            animation: `${keyframes `
                0% {
                    opacity: 0;
                    transform: translateX(-80%);
                }
                100% {
                    opacity: 1;
                    transform: translateX(0);
                }
            `} 0.2s forwards ease`,
            '&.unmount': {
                animation: `${keyframes `
                    0% {
                        opacity: 1;
                        transform: translateX(0);
                    }
                    100% {
                        opacity: 0;
                        transform: translateX(-80%);
                    }
                `} 0.2s forwards ease`
            }
        },
        ribbon: {
            width: '4px',
            height: 'calc(100% - 16px)',
            position: 'absolute',
            left: 0,
            top: '8px',
            borderRadius: '4px',
            '&.normal': {
                backgroundColor: theme.palette.infoToast
            },
            '&.error': {
                backgroundColor: theme.palette.primary12
            },
            '&.success': {
                backgroundColor: theme.palette.success01
            },
            '&.warning': {
                backgroundColor: theme.palette.warningToast
            }
        },
        content: {
            display: 'flex',
            alignItems: 'flex-start',
            padding: '8px 0',
            flex: 1,
            maxWidth: '100%',
            fontSize: '16px',
            '&path': {
                fill: 'none !important'
            },
            '&svg': {
                fill: 'none !important'
            }
        },
        textContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            color: theme.palette.text04,
            flex: 1,
            margin: '0 8px',
            // maxWidth: 100% minus the icon on left (20px) minus the close icon on the right (20px) minus the margins
            maxWidth: 'calc(100% - 40px - 16px)',
            maxHeight: '160px',
            fontSize: '16px'
        },
        title: {
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            fontSize: '16px'
        },
        description: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            overflow: 'auto',
            overflowWrap: 'break-word',
            userSelect: 'all',
            fontSize: '16px',
            '&:not(:empty)': {
                marginTop: theme.spacing(1)
            }
        },
        actionsContainer: {
            display: 'flex',
            width: '100%',
            justifyContent: 'right',
            fontSize: '16px',
            '&:not(:empty)': {
                marginTop: theme.spacing(2)
            }
        },
        action: {
            border: 0,
            outline: 0,
            backgroundColor: 'transparent',
            color: theme.palette.primary10,
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            // marginRight: theme.spacing(3),
            padding: '8px 10px',
            borderRadius: '40px',
            cursor: 'pointer',
            fontSize: '16px',
            '&:hover': {
                backgroundColor: theme.palette.bgParticipantHover
            },
            '&:last-of-type': {
                marginRight: 0
            },
            '&.destructive': {
                color: theme.palette.primary10
            }
        },
        closeIcon: {
            cursor: 'pointer',
            position: 'absolute',
            top: '16px',
            right: '16px'
        },
        notificationIconnormal: {
            '& svg': {
                fill: theme.palette.infoToast
            },
            '& g': {
                fill: theme.palette.infoToast
            },
            // '& path': {
            //     fill: 'none'
            // },
            // '& >svg': {
            //     fill: 'none'
            // }
        },
        notificationIconerror: {
            '& svg': {
                fill: theme.palette.iconError
            },
            '& g': {
                fill: theme.palette.iconError
            },
            '& path': {
                fill: theme.palette.iconError
            },
        },
        notificationIconsuccess: {
            '& svg': {
                fill: theme.palette.success01
            },
            '& g': {
                fill: theme.palette.success01
            },
            '& path': {
                fill: theme.palette.success01
            },
        },
        notificationIconwarning: {
            '& svg': {
                fill: theme.palette.warning01
            },
            '& g': {
                fill: theme.palette.warning01
            },
            '& path': {
                fill: theme.palette.warning01
            },
        }
    };
});
const Notification = ({ appearance = NOTIFICATION_TYPE.NORMAL, customActionClose, customActionHandler, customActionNameKey, customActionType, description, descriptionArguments, descriptionKey, disableClosing, hideErrorSupportLink, hideDismiss, icon, onDismissed, title, titleArguments, titleKey, uid, }) => {
    const { classes, cx, theme } = useStyles();
    const { t } = useTranslation();
    const { unmounting } = useContext(NotificationsTransitionContext);
    const ICON_COLOR = {
        error: theme.palette.iconError,
        normal: theme.palette.infoToast,
        success: theme.palette.success01,
        warning: theme.palette.warning01
    };
    const onDismiss = useCallback(() => {
        customActionClose && customActionClose();
        onDismissed(uid);
    }, [uid]);
    // eslint-disable-next-line react/no-multi-comp
    const renderDescription = useCallback(() => {
        const descriptionArray = [];
        descriptionKey
            && descriptionArray.push(t(descriptionKey, descriptionArguments));
        description && typeof description === 'string' && descriptionArray.push(description);
        // Keeping in mind that:
        // - Notifications that use the `translateToHtml` function get an element-based description array with one entry
        // - Message notifications receive string-based description arrays that might need additional parsing
        // We look for ready-to-render elements, and if present, we roll with them
        // Otherwise, we use the Message component that accepts a string `text` prop
        const shouldRenderHtml = descriptionArray.length === 1 && isValidElement(descriptionArray[0]);
        // the id is used for testing the UI
        return (React.createElement("div", { className: classes.description, "data-testid": descriptionKey },
            shouldRenderHtml ? descriptionArray : React.createElement("div", { className: 'notification-message' },
                React.createElement(Message, { text: descriptionArray.join(' ') })),
            typeof description === 'object' && description));
    }, [description, descriptionArguments, descriptionKey, classes]);
    const _onOpenSupportLink = () => {
        window.open(interfaceConfig.SUPPORT_URL, '_blank', 'noopener');
    };
    const mapAppearanceToButtons = useCallback(() => {
        switch (appearance) {
            case NOTIFICATION_TYPE.ERROR: {
                const buttons = [];
                // if(!hideDismiss) {
                //     buttons.push({
                //         content: t('dialog.dismiss'),
                //         onClick: onDismiss
                //     })
                // }
                // if (!hideErrorSupportLink && interfaceConfig.SUPPORT_URL) {
                //     buttons.push({
                //         content: t('dialog.contactSupport'),
                //         onClick: _onOpenSupportLink
                //     });
                // }
                return buttons;
            }
            case NOTIFICATION_TYPE.WARNING:
                if (!hideDismiss) {
                    return [
                        {
                            content: t('dialog.Ok'),
                            onClick: onDismiss
                        }
                    ];
                }
                return [];
            default:
                if (customActionNameKey?.length && customActionHandler?.length) {
                    return customActionNameKey.map((customAction, customActionIndex) => {
                        return {
                            content: t(customAction),
                            onClick: () => {
                                if (customActionHandler?.[customActionIndex]()) {
                                    onDismiss();
                                }
                            },
                            type: customActionType?.[customActionIndex],
                            testId: customAction
                        };
                    });
                }
                return [];
        }
    }, [appearance, onDismiss, customActionHandler, customActionNameKey, hideErrorSupportLink]);
    const getIcon = useCallback(() => {
        let iconToDisplay;
        switch (icon || appearance) {
            case NOTIFICATION_ICON.ERROR:
                iconToDisplay = IconErrorToast;
                break;
            case NOTIFICATION_ICON.WARNING:
                iconToDisplay = IconWarningToast;
                break;
            case NOTIFICATION_ICON.SUCCESS:
                iconToDisplay = IconCheck;
                break;
            case NOTIFICATION_ICON.MESSAGE:
                iconToDisplay = IconMessage;
                break;
            case NOTIFICATION_ICON.PARTICIPANT:
                iconToDisplay = IconLobbyUser;
                break;
            case NOTIFICATION_ICON.PARTICIPANTS:
                iconToDisplay = IconLobbyUsers;
                break;
            default:
                iconToDisplay = IconInfoToast;
                break;
        }
        return iconToDisplay;
    }, [icon, appearance]);
    return (React.createElement("div", { className: cx(classes.container, unmounting.get(uid ?? '') && 'unmount'), "data-testid": titleKey || descriptionKey, id: uid },
        React.createElement("div", { className: cx(classes.ribbon, appearance) }),
        React.createElement("div", { className: classes.content },
            React.createElement("div", { className: icon },
                React.createElement(Icon
                // @ts-expect-error
                , { 
                    // @ts-expect-error
                    className: classes[`notificationIcon${appearance}`], color: ICON_COLOR[appearance], size: 20, src: getIcon() })),
            React.createElement("div", { className: classes.textContainer },
                React.createElement("span", { className: classes.title }, title || t(titleKey ?? '', titleArguments)),
                renderDescription(),
                React.createElement("div", { className: classes.actionsContainer, style: { position: 'relative', right: -29 } }, mapAppearanceToButtons().map(({ content, onClick, type, testId }) => (React.createElement("button", { style: (content == t('participantsPane.actions.reject')) ? { color: '#A0616A' } : (content == t('lobby.chat')) ? { display: 'none' } : {}, className: cx(classes.action, type), "data-testid": testId, key: content, onClick: onClick }, content)))))),
        !disableClosing && (React.createElement(Icon, { className: classes.closeIcon, color: theme.palette.icon04, id: 'close-notification', onClick: onDismiss, size: 14, src: IconCloseLarge, testId: `${titleKey || descriptionKey}-dismiss` }))));
};
export default Notification;
